import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, fmtPKR } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import StatusPill from "../components/StatusPill";
import { toast } from "sonner";
import { ArrowLeft, Phone, MapPin, IdCard, ShieldCheck, ShieldAlert, Plus, FileText, BellRing, PhoneCall, MessageSquare, AlertTriangle, Edit, Trash2, Ban, Flag, ImageIcon, ChevronDown, ChevronRight } from "lucide-react";
import LoanDialog from "../components/LoanDialog";
import LoanCreatedDialog from "../components/LoanCreatedDialog";
import AgreementDialog from "../components/AgreementDialog";
import AgreementsSection from "../components/AgreementsSection";
import SendWhatsAppButton from "../components/SendWhatsAppButton";
import CustomerForm, { emptyCustomer, emptyGuarantor, RELATIONSHIPS } from "../components/CustomerForm";
import { RiskBadge, StatusBadge, FlagPill } from "../components/CustomerBadges";
import RiskBreakdown from "../components/RiskBreakdown";
import DocumentsCard from "../components/DocumentsCard";
import IdentityGallery from "../components/IdentityGallery";
import CustomerAvatar from "../components/CustomerAvatar";
import LedgerCard from "../components/LedgerCard";
import HardDeleteDialog from "../components/HardDeleteDialog";
import { useNavigate } from "react-router-dom";
import { useShopSettings } from "../lib/useShopSettings";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

export default function CustomerProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [risk, setRisk] = useState(null);
  const [riskOpen, setRiskOpen] = useState(false);
  const [consent, setConsent] = useState(false);
  const [note, setNote] = useState("");
  const [loanDialogOpen, setLoanDialogOpen] = useState(false);
  const [agreementOpen, setAgreementOpen] = useState(false);
  const [agreementLoanId, setAgreementLoanId] = useState(null);
  const [highRiskWarningOpen, setHighRiskWarningOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(emptyCustomer());
  const [agents, setAgents] = useState([]);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [createdLoan, setCreatedLoan] = useState(null);
  const { shop } = useShopSettings();
  const postCreateAction = shop?.loan_post_create_action || "auto_agreement";

  const load = () => api.get(`/customers/${id}`).then((r) => setData(r.data));
  useEffect(() => { load(); }, [id]);
  useEffect(() => {
    api.get("/agents").then((r) => setAgents(r.data || [])).catch(() => {});
  }, []);

  const openEdit = () => {
    if (!data) return;
    const c = data.customer;
    setEditForm({
      ...emptyCustomer(),
      ...c,
      guarantor1: c.guarantor1 || emptyGuarantor(),
      guarantor2: c.guarantor2 || null,
    });
    setEditOpen(true);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      const numFields = ["years_at_current_address", "monthly_house_rent", "monthly_income", "years_in_business"];
      const payload = { ...editForm };
      for (const k of numFields) payload[k] = payload[k] === "" || payload[k] == null ? null : Number(payload[k]);
      if (payload.guarantor2 && !payload.guarantor2.name) payload.guarantor2 = null;
      if (!payload.assigned_agent_id) payload.assigned_agent_id = null;
      await api.put(`/customers/${id}`, payload);
      toast.success("Customer updated");
      setEditOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };

  const updateAdminControl = async (patch, label) => {
    try {
      await api.put(`/customers/${id}/admin-control`, patch);
      toast.success(label || "Updated");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const verifyAction = async (patch, label) => {
    try {
      await api.put(`/customers/${id}/verify`, patch);
      toast.success(label || "Verified");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  // Auto-fetch risk signal as soon as customer loads (own-shop only, no consent needed for your own customer)
  useEffect(() => {
    if (!data?.customer?.mobile) return;
    api
      .post("/risk/check", { query: data.customer.mobile, consent: true })
      .then((r) => {
        setRisk(r.data);
        if (r.data?.risk_level === "high") {
          // Show the high-risk modal only once per session per customer to
          // avoid annoying the shop owner every time they revisit the profile.
          const dismissKey = `hp_hrwarn_${id}`;
          if (!sessionStorage.getItem(dismissKey)) {
            setHighRiskWarningOpen(true);
            sessionStorage.setItem(dismissKey, "1");
          }
        }
      })
      .catch(() => {});
  }, [data?.customer?.mobile, id]);

  const doRiskCheck = async () => {
    if (!consent) return toast.error("Please consent to continue.");
    try {
      const { data: r } = await api.post("/risk/check", {
        query: data.customer.mobile,
        consent: true,
      });
      setRisk(r);
      if (r.risk_level === "high") {
        setHighRiskWarningOpen(true);
        setRiskOpen(false);
      }
    } catch (e) {
      toast.error("Risk check failed");
    }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    await api.post("/followups", { customer_id: id, note });
    setNote("");
    toast.success("Note added");
    load();
  };

  const callStatus = async (status) => {
    await api.post("/followups", { customer_id: id, call_status: status, note: `Call status: ${status}` });
    toast.success("Call logged");
    load();
  };

  const sendReminder = async () => {
    await api.post(`/recovery/reminder/${id}`);
    toast.success("Reminder sent (simulated)");
    load();
  };

  if (!data) return <div className="p-4" data-testid="profile-loading">Loading…</div>;
  const c = data.customer;

  const riskColor =
    risk?.risk_level === "high"
      ? "bg-red-50 text-red-700 border-red-300"
      : risk?.risk_level === "medium"
      ? "bg-amber-50 text-amber-700 border-amber-300"
      : "bg-emerald-50 text-emerald-700 border-emerald-300";

  return (
    <div className="space-y-6" data-testid="customer-profile-page">
      <Link to="/customers" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900" data-testid="back-to-customers">
        <ArrowLeft className="w-4 h-4" /> All customers
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white border border-slate-200 rounded-md p-4 sm:p-6">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex gap-3 sm:gap-4 min-w-0 flex-1">
                {c.documents?.photo ? (
                  <img src={`${API_BASE}${c.documents.photo}`} alt={c.name}
                       className="w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover border-2 border-slate-200 shrink-0" />
                ) : (
                  <CustomerAvatar customer={c} size="xl" testId="profile-header-avatar" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Customer</div>
                  <h1 className="font-display text-2xl sm:text-3xl font-bold break-words">{c.name}</h1>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    <RiskBadge level={c.risk_level} score={c.risk_score} />
                    <StatusBadge status={c.verification_status || "pending"} />
                    {c.blacklisted && <FlagPill kind="blacklisted" />}
                    {c.flag_high_risk && !c.blacklisted && <FlagPill kind="flagged" />}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 text-sm text-slate-700">
                    <span className="inline-flex items-center gap-1.5 tabular"><Phone className="w-4 h-4 text-slate-400" />{c.mobile}</span>
                    {c.alternate_mobile && <span className="inline-flex items-center gap-1.5 tabular text-slate-500"><Phone className="w-4 h-4" />{c.alternate_mobile} (alt)</span>}
                    {c.cnic && <span className="inline-flex items-center gap-1.5 tabular"><IdCard className="w-4 h-4 text-slate-400" />{c.cnic}</span>}
                    {c.address && <span className="inline-flex items-center gap-1.5 break-words"><MapPin className="w-4 h-4 text-slate-400 shrink-0" />{c.address}</span>}
                  </div>
                </div>
              </div>
              <div className="flex flex-row sm:flex-col items-end gap-2 w-full sm:w-auto flex-wrap">
                <Button variant="outline" size="sm" onClick={openEdit} data-testid="edit-customer-btn">
                  <Edit className="w-4 h-4 mr-1" /> Edit
                </Button>
                <Button variant="outline" size="sm" onClick={() => setDeleteOpen(true)} className="border-red-200 text-red-700 hover:bg-red-50" data-testid="delete-customer-btn">
                  <Trash2 className="w-4 h-4 mr-1" /> Delete
                </Button>
                <Dialog open={riskOpen} onOpenChange={setRiskOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm" className="border-slate-300" data-testid="risk-check-btn">
                      <ShieldCheck className="w-4 h-4 mr-1.5" /> Network Risk
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-md" data-testid="risk-check-dialog">
                    <DialogHeader>
                      <DialogTitle>Customer Risk Check</DialogTitle>
                    </DialogHeader>
                    <div className="text-sm text-slate-600">
                      We will query the HisaabPro network for aggregated risk signals (late payment %,
                      active loans across shops, defaulter status). No shop-specific PII is shared.
                    </div>
                    <div className="flex items-start gap-2">
                      <Checkbox id="consent" checked={consent} onCheckedChange={setConsent} data-testid="risk-consent-checkbox" />
                      <Label htmlFor="consent" className="text-sm font-normal leading-relaxed">
                        I have customer consent to check risk on behalf of my shop.
                      </Label>
                    </div>
                    {risk && (
                      <div className={`mt-2 rounded-md border p-4 ${riskColor}`} data-testid="risk-result">
                        <div className="flex items-center justify-between">
                          <div className="font-display font-bold text-lg capitalize">{risk.risk_level} risk</div>
                          {risk.defaulter && <Badge className="bg-red-600 hover:bg-red-600">Defaulter</Badge>}
                        </div>
                        <div className="mt-2 text-sm grid grid-cols-2 gap-2">
                          <div><span className="text-slate-500">Active loans:</span> <span className="tabular font-semibold">{risk.active_loans}</span></div>
                          <div><span className="text-slate-500">Late payments:</span> <span className="tabular font-semibold">{risk.late_payment_pct}%</span></div>
                          <div className="col-span-2 text-xs text-slate-600 mt-1">{risk.message}</div>
                        </div>
                      </div>
                    )}
                    <DialogFooter>
                      <Button onClick={doRiskCheck} disabled={!consent} className="bg-emerald-600 hover:bg-emerald-700" data-testid="risk-run-btn">
                        Run Check
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
                {risk && (
                  <span
                    data-testid="risk-badge"
                    className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold border ${riskColor}`}
                  >
                    {risk.risk_level === "high" ? <ShieldAlert className="w-3 h-3" /> : <ShieldCheck className="w-3 h-3" />}
                    Risk: {risk.risk_level.toUpperCase()}
                    {risk.defaulter && <span className="ml-1 px-1 rounded bg-red-600 text-white text-[10px]">DEFAULTER</span>}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Customer Details Grid */}
          <DetailGrid customer={c} />

          {/* Risk Breakdown — explainable AI for shop owner */}
          <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="risk-breakdown-card">
            <div className="font-display font-semibold text-lg mb-3 flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-emerald-600" />
              Risk Profile
            </div>
            <RiskBreakdown customerId={id} />
          </div>

          {/* Guarantors */}
          <GuarantorsBlock customer={c} onVerify={verifyAction} apiBase={API_BASE} />

          {/* Photos & ID Documents — inline at-a-glance view */}
          <IdentityGallery customer={c} />

          {/* Customer Ledger — totals + PDF + WhatsApp */}
          <LedgerCard customerId={id} customer={c} />

          {/* Agreements list — quick access from the profile */}
          <AgreementsSection customerId={id} customer={c} />

          {/* Recovery Information (inline editable) */}
          <RecoveryInfoBlock customer={c} agents={agents} onUpdate={updateAdminControl} />

          {/* Documents */}
          <DocumentsCard customerId={id} customer={c} onUpdated={load} fullUrlBase={API_BASE} />

          {/* Admin Controls */}
          <AdminControlsBlock customer={c} onUpdate={updateAdminControl} onVerify={verifyAction} agents={agents} />

          {/* Loans */}
          <div className="bg-white border border-slate-200 rounded-md">
            <div className="flex items-center justify-between p-5 border-b border-slate-200">
              <div>
                <div className="font-display font-semibold text-lg">Loans</div>
                <div className="text-xs text-slate-500">{data.loans.length} loan(s)</div>
              </div>
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setLoanDialogOpen(true)} data-testid="new-loan-btn">
                <Plus className="w-4 h-4 mr-1" /> New Loan
              </Button>
            </div>
            <div className="divide-y divide-slate-100">
              {data.loans.length === 0 && (
                <div className="p-6 text-sm text-slate-500 text-center">No loans yet.</div>
              )}
              {data.loans.map((l) => (
                <div
                  key={l.id}
                  className="flex items-center justify-between p-5 hover:bg-slate-50"
                  data-testid={`loan-card-${l.id}`}
                >
                  <Link to={`/loans/${l.id}`} className="flex-1 flex items-center justify-between">
                    <div>
                      <div className="font-semibold">{l.product_name}</div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {fmtPKR(l.installment_value)} · {l.installment_type} · {l.duration} installments
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="tabular font-semibold">{fmtPKR(l.remaining_amount)}</div>
                      <div className="mt-1"><StatusPill status={l.current_status} daysLate={l.days_late} /></div>
                    </div>
                  </Link>
                  {l.status !== "closed" && (
                    <div className="ml-3 shrink-0" onClick={(e) => e.stopPropagation()}>
                      <SendWhatsAppButton
                        customer={data.customer}
                        loan={l}
                        variant="ghost"
                        testid={`loan-wa-${l.id}`}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Recovery sidebar */}
        <div className="space-y-6">
          <div className="bg-white border border-slate-200 rounded-md p-5">
            <div className="font-display font-semibold text-lg">Quick actions</div>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <Button variant="outline" className="justify-start" onClick={sendReminder} data-testid="send-reminder-btn">
                <BellRing className="w-4 h-4 mr-1.5 text-amber-600" /> Reminder
              </Button>
              <Button variant="outline" className="justify-start" onClick={() => callStatus("called")} data-testid="call-called-btn">
                <PhoneCall className="w-4 h-4 mr-1.5 text-emerald-600" /> Called
              </Button>
              <Button variant="outline" className="justify-start" onClick={() => callStatus("not_reachable")} data-testid="call-nr-btn">
                <PhoneCall className="w-4 h-4 mr-1.5 text-red-600" /> Not reachable
              </Button>
              <Button variant="outline" className="justify-start" onClick={() => callStatus("promised")} data-testid="call-promised-btn">
                <PhoneCall className="w-4 h-4 mr-1.5 text-sky-600" /> Promised
              </Button>
            </div>
            <div className="mt-4 space-y-2">
              <Label className="text-xs">Add follow-up note</Label>
              <Textarea rows={3} placeholder="e.g. Visited shop, promised to pay by Friday..." value={note} onChange={(e) => setNote(e.target.value)} data-testid="note-textarea" />
              <Button size="sm" onClick={addNote} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="save-note-btn">
                <MessageSquare className="w-4 h-4 mr-1" /> Save note
              </Button>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-md p-5">
            <div className="font-display font-semibold text-lg mb-3">Activity</div>
            {data.followups.length === 0 && <div className="text-sm text-slate-500">No activity yet.</div>}
            <ul className="space-y-3">
              {data.followups.map((f) => (
                <li key={f.id} className="text-sm border-l-2 border-emerald-500 pl-3">
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <FileText className="w-3.5 h-3.5" />
                    {new Date(f.created_at).toLocaleString()}
                    {f.reminder_sent && <Badge variant="secondary" className="bg-amber-100 text-amber-700 border-amber-200">reminder</Badge>}
                    {f.call_status && <Badge variant="secondary" className="bg-sky-100 text-sky-700 border-sky-200">{f.call_status}</Badge>}
                  </div>
                  <div className="text-slate-700 mt-0.5">{f.note}</div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <LoanDialog
        open={loanDialogOpen}
        onOpenChange={setLoanDialogOpen}
        customerId={id}
        onSaved={(created) => {
          load();
          if (created?.id) setCreatedLoan(created);
        }}
      />

      <LoanCreatedDialog
        open={!!createdLoan}
        onOpenChange={(v) => !v && setCreatedLoan(null)}
        loan={createdLoan}
        defaultAction={postCreateAction}
        onGenerateAgreement={() => {
          setAgreementLoanId(createdLoan?.id);
          setAgreementOpen(true);
          setCreatedLoan(null);
        }}
        onSkip={() => setCreatedLoan(null)}
      />

      <AgreementDialog
        open={agreementOpen}
        onOpenChange={setAgreementOpen}
        loanId={agreementLoanId}
        onLoanActivated={load}
      />

      {/* High-risk warning popup */}
      <AlertDialog open={highRiskWarningOpen} onOpenChange={setHighRiskWarningOpen}>
        <AlertDialogContent data-testid="high-risk-dialog">
          <AlertDialogHeader>
            <div className="w-12 h-12 rounded-full bg-red-100 border border-red-200 flex items-center justify-center mx-auto mb-2">
              <ShieldAlert className="w-6 h-6 text-red-600" />
            </div>
            <AlertDialogTitle className="text-center">High-risk customer</AlertDialogTitle>
            <AlertDialogDescription className="text-center space-y-2">
              <span className="block">
                This customer has a <span className="font-bold text-red-600">HIGH</span> risk score on the HisaabPro network.
              </span>
              {risk && (
                <span className="block rounded-md bg-red-50 border border-red-200 p-3 text-sm text-slate-700">
                  <span className="block tabular">
                    {risk.defaulter && "⚠ Marked as defaulter. "}
                    {risk.late_payment_pct}% late payments · {risk.active_loans} active loan(s) · max {risk.max_days_late || 0}d late
                  </span>
                </span>
              )}
              <span className="block text-xs text-slate-500 pt-1">
                Consider requesting higher down payment or shorter tenure.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700 w-full" data-testid="high-risk-ack-btn">
              Acknowledge
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-0" data-testid="edit-customer-dialog">
          <DialogHeader className="px-6 pt-6">
            <DialogTitle>Edit Customer</DialogTitle>
          </DialogHeader>
          <form onSubmit={saveEdit} className="px-6 pb-6 space-y-3">
            <CustomerForm value={editForm} onChange={setEditForm} agents={agents} customerId={id} onDocsUploaded={load} />
            <DialogFooter className="sticky bottom-0 bg-white pt-3 -mx-6 px-6 border-t border-slate-200">
              <Button type="button" variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" data-testid="cust-update-btn">
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <HardDeleteDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Customer"
        targetLabel={c?.name}
        endpoint={`/customers/${id}`}
        previewEndpoint={`/customers/${id}/deletion-preview`}
        description="Customer ke saare loans (closed), payments, follow-ups, agreements aur uploaded files permanently delete ho jayenge. Active loan hai to delete block ho jayega."
        onDeleted={() => { toast.success("Customer deleted."); navigate("/customers"); }}
      />
    </div>
  );
}

// ── helper components ─────────────────────────────────────────

function Section({ title, defaultOpen = true, children, testId }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white border border-slate-200 rounded-md" data-testid={testId}>
      <button type="button" onClick={() => setOpen(!open)}
              className="w-full flex items-center gap-2 p-4 text-left hover:bg-slate-50">
        <div className="flex-1 font-display font-semibold text-slate-900">{title}</div>
        {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
      </button>
      {open && <div className="p-5 border-t border-slate-100">{children}</div>}
    </div>
  );
}

function KV({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">{label}</div>
      <div className="text-sm text-slate-800 mt-0.5">{value || <span className="text-slate-400">—</span>}</div>
    </div>
  );
}

function DetailGrid({ customer: c }) {
  return (
    <Section title="Customer Details" testId="customer-details-section">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KV label="Father / Husband" value={c.father_husband_name} />
        <KV label="Near Landmark" value={c.near_landmark} />
        <KV label="Residence Type" value={(c.residence_type || "").replace(/_/g, " ")} />
        <KV label="Years at address" value={c.years_at_current_address} />
        <KV label="Monthly Rent" value={c.monthly_house_rent ? `PKR ${Number(c.monthly_house_rent).toLocaleString()}` : null} />
        <KV label="Electricity Meter #" value={c.electricity_meter_number} />
        <KV label="Gas Meter #" value={c.gas_meter_number} />
        <KV label="Account Number" value={c.account_number} />
        <KV label="Reference Acct #" value={c.reference_account_number} />
        <KV label="Inspector" value={c.inspector_name} />
        <KV label="Business Type" value={(c.business_type || "").replace(/_/g, " ")} />
        <KV label="Business Name" value={c.business_name} />
        <KV label="Monthly Income" value={c.monthly_income ? `PKR ${Number(c.monthly_income).toLocaleString()}` : null} />
        <KV label="Years in business" value={c.years_in_business} />
        <KV label="Designation" value={c.designation} />
        <KV label="Office" value={c.office_name} />
        <KV label="Office Phone" value={c.office_telephone} />
        <KV label="Recovery Zone" value={c.recovery_zone} />
      </div>
      {c.credit_history && (
        <div className="mt-5 pt-4 border-t border-slate-100 grid grid-cols-2 md:grid-cols-5 gap-4">
          <KV label="Previous Installments" value={c.credit_history.previous_installments} />
          <KV label="Running Installments" value={c.credit_history.running_installments} />
          <KV label="Late Payments" value={c.credit_history.late_payments_count} />
          <KV label="Defaults" value={c.credit_history.default_count} />
          <KV label="Last Recovery" value={c.credit_history.last_recovery_date ? new Date(c.credit_history.last_recovery_date).toLocaleDateString() : null} />
        </div>
      )}
    </Section>
  );
}

function GuarantorCard({ g, idx, onVerify }) {
  if (!g || !g.name) {
    if (idx === 1) {
      return (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-4 text-sm text-amber-800">
          <AlertTriangle className="w-4 h-4 inline mr-1" />
          <b>Guarantor 1 missing.</b> Edit customer to add a guarantor.
        </div>
      );
    }
    return null;
  }
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4" data-testid={`g${idx}-card`}>
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="font-display font-semibold text-slate-900">Guarantor {idx}</div>
        <div className="flex items-center gap-1.5">
          <StatusBadge status={g.verification_status || "pending"} />
          {g.relationship_strength && (
            <span className="text-[10px] uppercase font-bold text-slate-500">· {g.relationship_strength}</span>
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <KV label="Name" value={g.name} />
        <KV label="Relationship" value={g.relationship && RELATIONSHIPS.includes(g.relationship) ? g.relationship : g.relationship} />
        <KV label="Father / Husband" value={g.father_husband_name} />
        <KV label="CNIC" value={g.cnic} />
        <KV label="Mobile" value={g.mobile} />
        <KV label="Telephone" value={g.telephone} />
        <KV label="Designation" value={g.designation} />
        <div></div>
        <div className="col-span-2"><KV label="Residence Address" value={g.residence_address} /></div>
        <div className="col-span-2"><KV label="Office Address" value={g.office_address} /></div>
      </div>
      {g.verification_status !== "verified" && (
        <div className="mt-3 flex gap-2">
          <Button size="sm" onClick={() => onVerify(idx === 1 ? { guarantor1_verification: "verified" } : { guarantor2_verification: "verified" }, `Guarantor ${idx} verified`)}
                  className="bg-emerald-600 hover:bg-emerald-700" data-testid={`g${idx}-verify-btn`}>
            Mark Verified
          </Button>
          {["strong", "medium", "weak"].map((s) => (
            <Button key={s} size="sm" variant="outline"
                    onClick={() => onVerify(idx === 1 ? { guarantor1_strength: s } : { guarantor2_strength: s }, `Relationship: ${s}`)}
                    data-testid={`g${idx}-strength-${s}`}>
              {s}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}

function GuarantorsBlock({ customer: c, onVerify, apiBase }) {
  return (
    <Section title="Guarantor Information" testId="guarantors-section">
      <div className="space-y-3">
        <GuarantorCard g={c.guarantor1} idx={1} onVerify={onVerify} />
        {c.guarantor2 && <GuarantorCard g={c.guarantor2} idx={2} onVerify={onVerify} />}
      </div>
    </Section>
  );
}

function RecoveryInfoBlock({ customer: c, agents, onUpdate }) {
  const [zone, setZone] = useState(c.recovery_zone || "");
  const [agentId, setAgentId] = useState(c.assigned_agent_id || "");
  const [notes, setNotes] = useState(c.recovery_notes || "");
  const [fieldNotes, setFieldNotes] = useState(c.field_visit_notes || "");
  React.useEffect(() => {
    setZone(c.recovery_zone || "");
    setAgentId(c.assigned_agent_id || "");
    setNotes(c.recovery_notes || "");
    setFieldNotes(c.field_visit_notes || "");
  }, [c.recovery_zone, c.assigned_agent_id, c.recovery_notes, c.field_visit_notes]);

  const save = () => {
    onUpdate(
      {
        recovery_zone: zone,
        assigned_agent_id: agentId || null,
        recovery_notes: notes,
        field_visit_notes: fieldNotes,
      },
      "Recovery info saved",
    );
  };

  const dirty =
    zone !== (c.recovery_zone || "") ||
    (agentId || "") !== (c.assigned_agent_id || "") ||
    notes !== (c.recovery_notes || "") ||
    fieldNotes !== (c.field_visit_notes || "");

  const assignedAgent = agents.find((a) => a.id === agentId);

  return (
    <Section title="Recovery Information" testId="recovery-info-section">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label className="text-xs">Recovery Zone</Label>
          <Input
            value={zone}
            onChange={(e) => setZone(e.target.value)}
            placeholder="e.g., Zone 5 - Gulshan"
            data-testid="recovery-zone-input"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Assigned Recovery Agent</Label>
          <Select value={agentId || "__none__"} onValueChange={(v) => setAgentId(v === "__none__" ? "" : v)}>
            <SelectTrigger data-testid="recovery-agent-select"><SelectValue placeholder="— Not assigned —" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">— Not assigned —</SelectItem>
              {agents.map((a) => (
                <SelectItem key={a.id} value={a.id}>{a.name} · {a.recovery_zone || "no zone"}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {assignedAgent && (
            <div className="text-xs text-emerald-700 mt-1">
              Contact: {assignedAgent.email} {assignedAgent.mobile && `· ${assignedAgent.mobile}`}
            </div>
          )}
          {agents.length === 0 && (
            <div className="text-xs text-slate-500 mt-1">
              No agents yet. Add one in <a href="/settings" className="text-emerald-700 font-semibold underline">Settings → Recovery Agents</a>.
            </div>
          )}
        </div>
        <div className="space-y-1.5 md:col-span-2">
          <Label className="text-xs">Recovery Notes</Label>
          <Textarea
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Special instructions for the recovery team (best calling hours, sensitivities, etc.)"
            data-testid="recovery-notes-textarea"
          />
        </div>
        <div className="space-y-1.5 md:col-span-2">
          <Label className="text-xs">Field Visit Notes</Label>
          <Textarea
            rows={3}
            value={fieldNotes}
            onChange={(e) => setFieldNotes(e.target.value)}
            placeholder="Last visit observations — door not opened, paid in cash, etc."
            data-testid="recovery-field-notes-textarea"
          />
        </div>
      </div>
      <div className="mt-3 flex items-center gap-2">
        <Button
          size="sm"
          onClick={save}
          disabled={!dirty}
          className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40"
          data-testid="recovery-info-save-btn"
        >
          {dirty ? "Save Recovery Info" : "Saved"}
        </Button>
        {dirty && (
          <button
            type="button"
            onClick={() => {
              setZone(c.recovery_zone || "");
              setAgentId(c.assigned_agent_id || "");
              setNotes(c.recovery_notes || "");
              setFieldNotes(c.field_visit_notes || "");
            }}
            className="text-xs text-slate-500 hover:text-slate-800 font-semibold"
            data-testid="recovery-info-discard-btn"
          >
            Discard
          </button>
        )}
      </div>
    </Section>
  );
}

function AdminControlsBlock({ customer: c, onUpdate, onVerify, agents }) {
  const [notes, setNotes] = useState(c.internal_notes || "");
  React.useEffect(() => { setNotes(c.internal_notes || ""); }, [c.internal_notes]);
  return (
    <Section title="Admin Controls" testId="admin-controls-section">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          variant={c.blacklisted ? "default" : "outline"}
          className={c.blacklisted ? "bg-red-600 hover:bg-red-700" : ""}
          onClick={() => onUpdate({ blacklisted: !c.blacklisted }, c.blacklisted ? "Removed from blacklist" : "Blacklisted")}
          data-testid="toggle-blacklist-btn"
        >
          <Ban className="w-4 h-4 mr-1" /> {c.blacklisted ? "Un-blacklist" : "Blacklist"}
        </Button>
        <Button
          size="sm"
          variant={c.flag_high_risk ? "default" : "outline"}
          className={c.flag_high_risk ? "bg-amber-500 hover:bg-amber-600 text-white" : ""}
          onClick={() => onUpdate({ flag_high_risk: !c.flag_high_risk }, c.flag_high_risk ? "Flag removed" : "Flagged High Risk")}
          data-testid="toggle-flag-btn"
        >
          <Flag className="w-4 h-4 mr-1" /> {c.flag_high_risk ? "Remove Flag" : "Flag High Risk"}
        </Button>
        <Button
          size="sm"
          variant={c.verification_status === "verified" ? "outline" : "default"}
          className={c.verification_status !== "verified" ? "bg-emerald-600 hover:bg-emerald-700" : ""}
          onClick={() => onVerify({ verification_status: c.verification_status === "verified" ? "pending" : "verified" }, "Verification updated")}
          data-testid="toggle-verify-btn"
        >
          <ShieldCheck className="w-4 h-4 mr-1" /> {c.verification_status === "verified" ? "Mark Pending" : "Mark Verified"}
        </Button>
      </div>

      <div className="mt-4 space-y-1.5">
        <Label className="text-xs">Internal notes (admin only)</Label>
        <Textarea
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Investigation findings, red flags, payment patterns…"
          data-testid="internal-notes-textarea"
        />
        <Button
          size="sm"
          onClick={() => onUpdate({ internal_notes: notes }, "Notes saved")}
          className="bg-slate-900 hover:bg-slate-800 text-white"
          data-testid="save-internal-notes-btn"
        >
          Save notes
        </Button>
      </div>
    </Section>
  );
}
